import { Injectable } from '@angular/core';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { BehaviorSubject, Observable, Subscription, tap } from 'rxjs';
import {
  Notice,
  NoticeFindAllParams,
  NoticeStatus,
} from '@/app/modules/notification/core/types';
import { NotificationApiService } from '@/app/modules/notification/infra/api.service';
import { WebsocketService } from '@/app/modules/websocket/core/service';

const NOTICE_TOPIC = 'notices';
const NOTICE_COUNT_TOPIC = 'notices:count';

@Injectable({ providedIn: 'root' })
export class NotificationCenterService {
  private unreadCountSubject = new BehaviorSubject<number>(0);
  private subscription = new Subscription();
  private active = false;

  constructor(
    private notificationApi: NotificationApiService,
    private websocketService: WebsocketService,
    private notification: NzNotificationService
  ) {}

  get unreadCount$() {
    return this.unreadCountSubject.asObservable();
  }

  bootstrap() {
    if (this.active || !localStorage.getItem('token')) {
      return;
    }

    this.active = true;
    this.bindRealtimeListeners();
    this.refreshUnreadCount();
    this.websocketService.connect();

    this.subscription.add(
      this.websocketService.connectionStatus.subscribe((status) => {
        if (status === 'connected') {
          this.bindRealtimeListeners();
        }
      })
    );
  }

  disconnect() {
    this.websocketService.disconnect();
    this.active = false;
    this.unreadCountSubject.next(0);
  }

  loadNotices(filter?: NoticeFindAllParams) {
    return this.notificationApi.findAll(filter);
  }

  markAsViewed(noticeId: number): Observable<Notice> {
    return this.notificationApi.setStatus(noticeId, NoticeStatus.VIEW);
  }

  markAllAsRead(): Observable<{ success: boolean }> {
    return this.notificationApi.markAllAsRead().pipe(
      tap(() => {
        this.unreadCountSubject.next(0);
        this.refreshUnreadCount();
      })
    );
  }

  refreshUnreadCount() {
    this.subscription.add(
      this.notificationApi.getUnreadCount().subscribe({
        next: ({ unread }) => this.unreadCountSubject.next(unread),
      })
    );
  }

  private bindRealtimeListeners() {
    this.websocketService.addListener(NOTICE_TOPIC, (data) => {
      const notice = data as Notice;
      this.showIncomingToast(notice);
      this.refreshUnreadCount();
    });

    this.websocketService.addListener(NOTICE_COUNT_TOPIC, (data) => {
      const payload = data as { unread: number };
      this.unreadCountSubject.next(payload.unread);
    });
  }

  private showIncomingToast(notice: Notice) {
    this.notification.success(notice.title, notice.content, {
      nzDuration: 6000,
      nzPlacement: 'topRight',
    });
  }
}
